/** @typedef {Object} FocusElement
 *  @property {string} id
 *  @property {HTMLElement} element
 */
var Focus = {
  debug: false,
  bullet: '[Focus] ',

  /**
   * Registered elements map
   */
  map: {},

  /**
   * Class to use when an element is focused
   */
  focusClass: 'focus',

  /**
   * Enable animations scrolling
   */
  autoScroll: false,
  animate: false,
  animationSpeed: 500,
  _oldBranch: undefined,
  _oldElement: {
    id: undefined,
    element: undefined
  },
  _curBranch: undefined,
  _curElement: {
    id: undefined,
    element: undefined
  },

  /**
   * Register the elements within their handlers in the specified viewId.
   * It will override the tree branch in case that the view_name already exists.
   *
   * Example:
   *
   * Focus.register({ element: ['upElement', 'rightElement', 'downElement', 'leftElement', clickFunction]}, 'view1');
   *
   * @param jsonObj The json Object containing the mapping for the elements
   * @param view_name The name to identify the elements group
   * @param override If the elements group should be override by the new jsonObj
   */
  register: function register(jsonObj, view_name, override) {
    if (override === undefined) override = true;

    if (!override) {
      for (var view in Focus.map) {
        if (view === view_name) return;
      }
    }

    for (var key in jsonObj) {
      if (!Focus.map[view_name]) Focus.map[view_name] = {};
      Focus.map[view_name][key] = jsonObj[key];
    }

    key = null;
  },

  /**
   * Unregister the specified ViewID from the Focus Map
   * @param viewId The view to unregister
   */
  unregister: function unregister(viewId) {
    delete Focus.map[viewId];
  },

  /**
   * Refocus the current element, applying the focus class if missing.
   */
  refocus: function refocus() {
    if (Focus._curElement && Focus._curElement.id && Focus._curBranch) {
      Focus._curElement.element = document.getElementById(Focus._curElement.id) || Focus._curElement.element;
      Focus.moveTo(Focus._curElement.id, Focus._curBranch);
    }
  },

  /**
   * Move the focus to another mapped element.
   *
   * @param elementId Element ID to move the focus to
   * @param viewId View in which the Element is, Optional but recommended
   */
  moveTo: function moveTo(elementId, viewId) {
    viewId = viewId || Focus._getViewsFromElement(elementId)[0] || undefined;

    if (!viewId) {
      console.error(Focus.bullet + "We couldn't find a valid branch for " + elementId);
      return;
    }

    if (Focus._curElement.id === elementId && Focus._curBranch === viewId) {
      try {
        Focus._addFocusClassToElement(Focus._curElement.element);
      } catch (e) {
        console.error(Focus.bullet + "We couldn't apply the focus to " + elementId, e);
      }

      return;
    }

    if (Focus._curElement.id) {
      try {
        Focus._removeFocusClassFromElement(Focus._curElement.element);
      } catch (e) {
        console.error(Focus.bullet + "We couldn't remove the focus from " + elementId, e);
      }
    }

    if (Focus._curBranch !== viewId) {
      Focus._oldBranch = Focus._curBranch;
      Focus._oldElement = Focus._curElement;
    }

    var nextElement = document.getElementById(elementId); // console.log("Move from", Focus._curElement.id, "to", elementId);

    if (!nextElement) {
      if (Focus.debug) {
        console.info(Focus.bullet + "Couldn't find " + elementId + ' - Focus got lost');
      }

      Focus._curElement = {
        id: undefined,
        element: undefined
      };
    } else {
      if (Focus.debug) {
        console.info(Focus.bullet + 'Properly moved from ' + Focus._curElement.id + ' to ' + elementId);
      }

      Focus._curElement = {
        id: elementId,
        element: nextElement
      };

      try {
        Focus._addFocusClassToElement(Focus._curElement.element);
      } catch (e) {
        console.error(Focus.bullet + "We couldn't apply the focus to " + elementId, e);
      }

      if (this.autoScroll) {
        var view_element = document.getElementById(viewId);

        if (view_element && typeof window !== 'undefined') {
          if (view_element.scrollHeight > window.innerHeight) {
            if (Focus.animate) {
              scrollToY(getElementOffsetTop(Focus._curElement.element) - window.innerHeight / 2, Focus.animationSpeed);
            } else {
              scrollToY(getElementOffsetTop(Focus._curElement.element) - window.innerHeight / 2, 0);
            }
          }
        }

        view_element = null;
      }

      Focus._curBranch = viewId; // Focus._curElement.element.scrollIntoView();

      nextElement = null;
    }
  },

  /**
   * Change the class that the focus system will be using.
   * The default class is 'focus'.
   *
   * @param focus_class
   */
  setFocusClass: function setFocusClass(focus_class) {
    if (typeof focus_class === 'string') {
      Focus.focusClass = focus_class;
    }
  },

  /**
   * Moves the focus to the element and, if it exists, force a click on it.
   *
   * @param elementId
   * @param viewId
   */
  clickTo: function clickTo(elementId, viewId) {
    Focus.moveTo(elementId, viewId);

    if (Focus._curElement.id) {
      Focus._handleEnter();
    }
  },

  /**
   * Get the old view in which the focus was before view changing
   * @deprecated
   *
   * @returns {undefined | *} Usually a string
   */
  getOldView: function getOldView() {
    return Focus._oldBranch;
  },

  /**
   * Get the old view in which the focus was before view changing
   *
   * @returns {undefined | *} Usually a string
   */
  getOldBranch: function getOldBranch() {
    return Focus._oldBranch;
  },

  /**
   * Get the old navigation element in which the focus was before view changing
   * @deprecated
   *
   * @returns {FocusElement} id and dom element
   */
  getOldNav: function getOldNav() {
    return Focus._oldElement;
  },

  /**
   * Get the old navigation element in which the focus was before view changing
   *
   * @returns {FocusElement} id and dom element
   */
  getOldElement: function getOldElement() {
    return Focus._oldElement;
  },

  /**
   * Get the current branch in which the focus is
   * @deprecated
   *
   * @returns {undefined | *} Usually a string
   */
  getCurrView: function getCurrView() {
    return Focus._curBranch;
  },

  /**
   * Get the current branch in which the focus is
   *
   * @returns {undefined | *} Usually a string
   */
  getCurrentBranch: function getCurrentBranch() {
    return Focus._curBranch;
  },

  /**
   * Get the current navigation element in which the focus is
   * @deprecated
   *
   * @returns {FocusElement} id and dom element
   */
  getCurrNav: function getCurrNav() {
    return Focus._curElement;
  },

  /**
   * Get the current navigation element in which the focus is
   *
   * @returns {FocusElement} id and dom element
   */
  getCurrentElement: function getCurrentElement() {
    return Focus._curElement;
  },

  /**
   * Launch the Focus UP case
   */
  toUp: function toUp() {
    Focus._handleUp();
  },

  /**
   * Launch the Focus RIGHT case
   */
  toRight: function toRight() {
    Focus._handleRight();
  },

  /**
   * Launch the Focus DOWN case
   */
  toDown: function toDown() {
    Focus._handleDown();
  },

  /**
   * Launch the Focus LEFT case
   */
  toLeft: function toLeft() {
    Focus._handleLeft();
  },

  /**
   * Launch the Focus CLICK case
   */
  click: function click() {
    Focus._handleEnter();
  },

  /**
   * Change the opacity to the focus element to 0
   */
  hide: function hide() {
    Focus._curElement.element.style.opacity = '0';
  },

  /**
   * Change the opacity to the focus element to 1
   */
  show: function show() {
    Focus._curElement.element.style.opacity = '1';
  },
  _handleUp: function _handleUp() {
    Focus._handleMove(0);
  },
  _handleRight: function _handleRight() {
    Focus._handleMove(1);
  },
  _handleDown: function _handleDown() {
    Focus._handleMove(2);
  },
  _handleLeft: function _handleLeft() {
    Focus._handleMove(3);
  },
  _handleEnter: function _handleEnter() {
    Focus._handleMove(4);
  },
  _handleMove: function _handleMove(arrayPos) {
    if (!Focus._curElement.id || !Focus._curBranch) return;
    var elemArray = Focus.map[Focus._curBranch][Focus._curElement.id];

    try {
      var toDo = elemArray[arrayPos];
      if (typeof toDo === 'function') toDo();else if (toDo) Focus.moveTo(toDo, Focus._curBranch);else if (toDo == undefined) throw 'Action not registered';
    } catch (e) {
      console.error(Focus.bullet + "Looks like the element can't handle that action.\nElement: " + Focus._curElement.id + '\nMap branch: ' + Focus._curBranch + '\nElement Actions: ' + Focus._getStringifiedActions(elemArray) + '\nAction Position: ' + arrayPos + ' - ' + Focus._getActionString(arrayPos));
      console.error('Exception >>', e);
    }

    toDo = null;
    elemArray = null;
  },
  _getStringifiedActions: function _getStringifiedActions(actions) {
    var parsedArray = [];

    for (var i = 0; i < actions.length; i++) {
      var action = actions[i];

      if (typeof action == 'string') {
        parsedArray.push(action);
      } else if (typeof action == 'function') {
        var functionName = action.name || 'callback';
        parsedArray.push('ϝ(' + functionName + ')');
      } else {
        parsedArray.push(action);
      }
    }

    return JSON.stringify(parsedArray);
  },
  _getActionString: function _getActionString(direction) {
    switch (direction) {
      case 0:
        return 'UP';

      case 1:
        return 'RIGHT';

      case 2:
        return 'DOWN';

      case 3:
        return 'LEFT';

      case 4:
        return 'CLICK';

      default:
        return direction + '?';
    }
  },
  _getViewsFromElement: function _getViewsFromElement(elementId) {
    var arr = [];

    for (var view in Focus.map) {
      for (var elid in Focus.map[view]) {
        if (elementId === elid) {
          arr.push(view);
        }
      }
    }

    view = null;
    elid = null;
    return arr;
  },
  _addFocusClassToElement: function _addFocusClassToElement(element) {
    var arr = element.className.split(' ');

    if (arr.indexOf(Focus.focusClass) === -1) {
      element.className += (arr.length >= 1 ? arr[arr.length - 1] ? ' ' : '' : '') + Focus.focusClass;
    }

    arr = null;
  },
  _removeFocusClassFromElement: function _removeFocusClassFromElement(element) {
    Focus._curElement.element.className = element.className.replace(new RegExp('\\b ?' + Focus.focusClass + '\\b', 'g'), '');
  }
};

function getElementOffsetTop(element) {
  return element.offsetTop + (element.offsetParent ? getElementOffsetTop(element.offsetParent) : 0);
}

if (typeof window !== 'undefined') {
  // add raf polyfill
  window.requestAnimFrame = function () {
    return window.requestAnimationFrame || window.webkitRequestAnimationFrame || window.mozRequestAnimationFrame || function (callback) {
      window.setTimeout(callback, 1000 / 60);
    };
  }();
}

function scrollToY(scrollTargetY, speed, easing) {
  // scrollTargetY: the target scrollY property of the window
  // speed: time in pixels per second
  // easing: easing equation to use
  var scrollY = typeof window !== 'undefined' ? window.scrollY : 0,
      currentTime = 0;
  scrollTargetY = scrollTargetY || 0;
  speed = speed || 2000;
  easing = easing || 'easeOutSine'; // min time .1, max time .8 seconds

  var time = Math.max(0.1, Math.min(Math.abs(scrollY - scrollTargetY) / speed, 0.8)); // easing equations from https://github.com/danro/easing-js/blob/master/easing.js

  var easingEquations = {
    easeOutSine: function easeOutSine(pos) {
      return Math.sin(pos * (Math.PI / 2));
    },
    easeInOutSine: function easeInOutSine(pos) {
      return -0.5 * (Math.cos(Math.PI * pos) - 1);
    },
    easeInOutQuint: function easeInOutQuint(pos) {
      if ((pos /= 0.5) < 1) {
        return 0.5 * Math.pow(pos, 5);
      }

      return 0.5 * (Math.pow(pos - 2, 5) + 2);
    }
  }; // add animation loop

  function tick() {
    currentTime += 1 / 60;
    var p = currentTime / time;
    var t = easingEquations[easing](p);

    if (p < 1) {
      if (typeof window !== 'undefined') window.requestAnimFrame(tick);
      if (typeof window !== 'undefined') window.scrollTo(0, scrollY + (scrollTargetY - scrollY) * t);
    } else {
      if (typeof window !== 'undefined') window.scrollTo(0, scrollTargetY);
    }
  }

  tick();
}

export default Focus;